import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Shield, Store, Sparkles, ArrowRight, Info } from "lucide-react";
import { Logo } from "../components/Logo";
import { LanguageToggle } from "../components/LanguageToggle";
import { useI18n } from "../lib/i18n";
import { useAuth, formatApiError } from "../context/AuthContext";

const QUICK = [
  { role: "admin", icon: Shield, key: "demoAdmin", tone: "from-amber-400/20 text-amber-300 border-amber-400/40" },
  { role: "commerce", icon: Store, key: "demoBusiness", tone: "from-sky-400/20 text-sky-300 border-sky-400/40" },
  { role: "creator", icon: Sparkles, key: "demoCreator", tone: "from-fuchsia-400/20 text-fuchsia-300 border-fuchsia-400/40" },
];

export default function Login() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const { user, login, register, quickLogin } = useAuth();
  const [mode, setMode] = useState("login");
  const [form, setForm] = useState({ email: "", password: "", name: "", role: "creator" });
  const [loading, setLoading] = useState(false);
  const [busyRole, setBusyRole] = useState(null);

  useEffect(() => {
    if (user) navigate(user.role === "admin" ? "/admin" : "/app", { replace: true });
  }, [user, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const u = mode === "login" ? await login(form.email, form.password) : await register(form);
      toast.success(`${t("welcomeBack")}, ${u.name}`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const doQuick = async (role) => {
    setBusyRole(role);
    try {
      const u = await quickLogin(role);
      toast.success(`${u.name}`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setBusyRole(null);
    }
  };

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  return (
    <div className="relative min-h-screen bg-ink text-white">
      <div className="pointer-events-none fixed inset-0 grain opacity-40" />
      <div className="absolute -top-24 left-1/2 h-72 w-72 -translate-x-1/2 rounded-full bg-gold/10 blur-3xl" />
      <header className="relative flex items-center justify-between px-4 py-3">
        <button onClick={() => navigate("/")} data-testid="login-back-logo"><Logo size={28} /></button>
        <LanguageToggle />
      </header>

      <motion.div
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="relative mx-auto mt-4 w-full max-w-md px-4"
      >
        <div className="rounded-3xl border border-cardline bg-card2/70 p-6 backdrop-blur-xl">
          <h1 className="font-heading text-2xl font-extrabold">{t("welcomeBack")} 👋</h1>
          <p className="mt-1 text-sm text-slate-400">{t("loginSubtitle")}</p>

          {/* Quick demo access */}
          <div className="mt-5">
            <div className="mb-2 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-slate-400">
              <Sparkles size={13} className="text-gold" /> {t("quickAccess")}
            </div>
            <div className="grid grid-cols-3 gap-2" data-testid="quick-login-group">
              {QUICK.map(({ role, icon: Icon, key, tone }) => (
                <button
                  key={role}
                  onClick={() => doQuick(role)}
                  disabled={busyRole === role}
                  data-testid={`quick-login-${role}`}
                  className={`flex flex-col items-center gap-1.5 rounded-2xl border bg-gradient-to-b to-transparent px-2 py-3 text-[11px] font-semibold transition-transform hover:scale-[1.03] disabled:opacity-50 ${tone}`}
                >
                  <Icon size={18} />
                  <span>{t(key)}</span>
                </button>
              ))}
            </div>
            <div className="mt-2 flex items-start gap-1.5 rounded-xl bg-ink2/60 p-2.5 text-[11px] text-slate-400">
              <Info size={13} className="mt-0.5 shrink-0 text-gold" />
              <span data-testid="prelaunch-notice">{t("prelaunchNotice")}</span>
            </div>
          </div>

          <div className="my-5 flex items-center gap-3">
            <div className="h-px flex-1 bg-cardline" />
            <span className="text-xs text-slate-500">— {t("email")} —</span>
            <div className="h-px flex-1 bg-cardline" />
          </div>

          <form onSubmit={submit} className="space-y-3">
            {mode === "register" && (
              <>
                <input
                  data-testid="register-name-input"
                  required
                  placeholder={t("name")}
                  value={form.name}
                  onChange={set("name")}
                  className="w-full rounded-xl border border-cardline bg-ink2/60 px-4 py-3 text-sm outline-none focus:border-gold"
                />
                <div className="grid grid-cols-2 gap-2">
                  {[["creator", t("roleCreator")], ["commerce", t("roleBusiness")]].map(([v, label]) => (
                    <button
                      type="button"
                      key={v}
                      onClick={() => setForm((f) => ({ ...f, role: v }))}
                      data-testid={`role-${v}`}
                      className={`rounded-xl border px-3 py-2.5 text-sm font-semibold transition-colors ${
                        form.role === v ? "border-gold bg-gold/15 text-gold" : "border-cardline text-slate-300"
                      }`}
                    >
                      {label}
                    </button>
                  ))}
                </div>
              </>
            )}
            <input
              data-testid="login-email-input"
              required
              type="email"
              placeholder={t("email")}
              value={form.email}
              onChange={set("email")}
              className="w-full rounded-xl border border-cardline bg-ink2/60 px-4 py-3 text-sm outline-none focus:border-gold"
            />
            <input
              data-testid="login-password-input"
              required
              type="password"
              placeholder={t("password")}
              value={form.password}
              onChange={set("password")}
              className="w-full rounded-xl border border-cardline bg-ink2/60 px-4 py-3 text-sm outline-none focus:border-gold"
            />
            <button
              type="submit"
              disabled={loading}
              data-testid="login-submit-btn"
              className="flex w-full items-center justify-center gap-2 rounded-xl bg-gold py-3 font-bold text-ink transition-transform hover:scale-[1.01] disabled:opacity-60"
            >
              {loading ? t("loading") : mode === "login" ? t("login") : t("createAccount")}
              {!loading && <ArrowRight size={18} />}
            </button>
          </form>

          <div className="mt-4 text-center text-sm text-slate-400">
            {mode === "login" ? t("noAccount") : t("haveAccount")}{" "}
            <button
              onClick={() => setMode(mode === "login" ? "register" : "login")}
              data-testid="toggle-auth-mode"
              className="font-semibold text-gold"
            >
              {mode === "login" ? t("createAccount") : t("login")}
            </button>
          </div>
        </div>
      </motion.div>
    </div>
  );
}
